import { RefObject, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IKnmType } from 'APIPackage/CatalogsModule/interfaces/IKnmType';
import { HEADER_STICKED_HEIGHT } from 'CorePackage/HeaderModule/constants/headerStickedHeight';
import { Sidebar } from 'CorePackage/MainModule/components/Sidebar';
import { ROUTE_KNM, ROUTE_KNM_FROM_ANALYTICS_ID } from 'CorePackage/MainModule/routes/routes';
import {
  getKnmDataCreated,
  getKnmDataLateChangesFields,
  getKnmPath,
  getKnmUrlId,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';

import { basicErknmMenuItems } from '../constants/basicErknmMenuItems';
import { ErknmMenuItemsIdsEnum } from '../enums/ErknmMenuItemsIdsEnum';
import { IKnmErknmMenuItem } from '../interfaces/IKnmErknmMenuItem';
import { IKnmHistoryItem } from '../../../APIPackage/KnmModule/interfaces/IKnmHistoryItem';
import { getLateFiledLinkedName } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/common/getLateFiledLinkedName';

interface IAnchor {
  route: string;
  currentAnchorElement: HTMLElement | null;
  nextAnchorElement: HTMLElement | null;
}

interface IKnmMenuProps {
  dataRef: RefObject<HTMLDivElement>;
  hiddenItemsIds?: ErknmMenuItemsIdsEnum[];
  knmType?: IKnmType | null;
  lateChangesFieldLinks: Map<string, string>;
}

interface IKnmErknmMenuStoreProps {
  knmUrlId: string | undefined;
  lateChangesFields: IKnmHistoryItem[] | null | undefined;
  path: string | undefined;
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
}

const KnmErknmMenuWithoutStore = ({
  dataRef,
  hiddenItemsIds,
  knmType,
  knmUrlId,
  lateChangesFields,
  lateChangesFieldLinks,
  path,
  created,
  settings,
}: IKnmMenuProps & IKnmErknmMenuStoreProps) => {
  const [locationHash, setLocationHash] = useState(window.location.hash);

  const menuItemWarning = (menuItem: IKnmErknmMenuItem) => {
    const actLateChangesPath = '/act/';
    const resultDecisionsLateChangesPath = '/resultDecisions/';

    if (!lateChangesFields) {
      return menuItem.warning;
    }

    const lateChanges = lateChangesFields.flatMap(x => x.changes);

    if (
      menuItem.id === ErknmMenuItemsIdsEnum.KNM_ACT &&
      lateChanges?.find(x => x?.field.includes(actLateChangesPath))
    ) {
      return getLateFiledLinkedName(lateChangesFields, lateChangesFieldLinks, actLateChangesPath);
    }

    if (
      menuItem.id === ErknmMenuItemsIdsEnum.KNM_RESULT_DECISIONS &&
      lateChanges?.find(x => x?.field.includes(resultDecisionsLateChangesPath))
    ) {
      return getLateFiledLinkedName(lateChangesFields, lateChangesFieldLinks, resultDecisionsLateChangesPath);
    }

    return menuItem.warning;
  };

  const menuItems = useMemo(
    () =>
      basicErknmMenuItems(created, settings).reduce((newMenuItems: IKnmErknmMenuItem[], basicMenuItem) => {
        if (hiddenItemsIds?.some(hiddenItemId => hiddenItemId === basicMenuItem.id)) {
          return newMenuItems;
        }

        if (lateChangesFields && lateChangesFieldLinks?.size > 0) {
          newMenuItems.push({
            id: basicMenuItem.id,
            route: basicMenuItem.route,
            title: basicMenuItem.title,
            warning: menuItemWarning(basicMenuItem),
          });
        } else {
          newMenuItems.push(basicMenuItem);
        }

        return newMenuItems;
      }, []),
    [hiddenItemsIds, lateChangesFields, lateChangesFieldLinks.size],
  );
  const erknmMenuItems =
    knmType?.code === KnmTypeCodesEnum.PP248P294
      ? menuItems.filter(item => item.id !== ErknmMenuItemsIdsEnum.KNM_SIGN_INFO)
      : menuItems;

  const [anchors, setAnchors] = useState<IAnchor[]>([]);

  useEffect(() => {
    setAnchors(
      menuItems.map((item, i) => {
        const isLast = menuItems.length === i + 1;
        const anchorId = item.route.replace('#', '');
        const currentAnchorElement = document.getElementById(anchorId);
        const nextAnchorId = !isLast ? menuItems[i + 1].route.replace('#', '') : null;
        const nextAnchorElement = nextAnchorId ? document.getElementById(nextAnchorId) : null;

        return {
          route: item.route,
          currentAnchorElement,
          nextAnchorElement,
        };
      }),
    );
  }, [menuItems]);

  useEffect(() => {
    const updateLocationHash = () => {
      const refOffset = dataRef.current?.getBoundingClientRect().top;
      const templateHash = window.location.hash;

      if (refOffset || refOffset === 0) {
        if (refOffset < HEADER_STICKED_HEIGHT) {
          const isPageBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;

          if (isPageBottom && anchors.length > 0) {
            const lastRoute = anchors[anchors.length - 1].route;

            if (window.location.hash !== lastRoute) {
              window.history.replaceState(null, '', lastRoute);
            }
          } else {
            anchors.forEach(({ route, currentAnchorElement, nextAnchorElement }) => {
              const currentAnchorOffsetTop = currentAnchorElement?.getBoundingClientRect().top!;
              const nextAnchorOffsetTop = nextAnchorElement?.getBoundingClientRect().top!;

              if (
                currentAnchorOffsetTop <= HEADER_STICKED_HEIGHT &&
                nextAnchorOffsetTop > HEADER_STICKED_HEIGHT &&
                window.location.hash !== route
              ) {
                window.history.replaceState(null, '', route);
              }
            });
          }
        } else if (templateHash && knmUrlId) {
          if (path?.includes('/analytics/')) {
            window.history.replaceState(null, '', ROUTE_KNM_FROM_ANALYTICS_ID(knmUrlId));
          } else {
            window.history.replaceState(null, '', ROUTE_KNM(knmUrlId));
          }
        }
      }
      setLocationHash(templateHash);
    };

    updateLocationHash();

    window.addEventListener('scroll', updateLocationHash);

    return () => window.removeEventListener('scroll', updateLocationHash);
  }, [anchors, knmUrlId, dataRef]);

  return (
    <Sidebar
      type="knm-menu"
      menuItems={erknmMenuItems}
      locationHash={locationHash}
      itemsIsAnchors
      useClickableWarning={true}
    />
  );
};

const mapStateToProps = (state: IRootState): IKnmErknmMenuStoreProps => ({
  knmUrlId: getKnmUrlId(state),
  path: getKnmPath(state),
  lateChangesFields: getKnmDataLateChangesFields(state),
  created: getKnmDataCreated(state),
  settings: getAdminFetchSettings(state),
});

export const KnmErknmMenu = connect(mapStateToProps)(KnmErknmMenuWithoutStore);
